import React, { useEffect, useState } from 'react';
import { Plus, ChevronDown, ChevronUp, Trash2, Link2, Unlink } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { workflowAPI } from '../utils/api';
import { toast } from 'sonner';

const STAGES = [
  { key: 'photo_selection', label: 'Photo Selection' },
  { key: 'design', label: 'Design' },
  { key: 'client_review', label: 'Client Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'sent_to_print', label: 'Sent to Print' },
  { key: 'delivered', label: 'Delivered' },
];

const AlbumProcessing = ({ embedded }) => {
  const [albums, setAlbums] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedAlbum, setExpandedAlbum] = useState(null);
  const [linkingAlbum, setLinkingAlbum] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    client_name: '',
    event_type: '',
    album_name: '',
    notes: '',
    useProject: false,
    useEvent: false,
  });

  // Group projects by year for dropdown
  const getProjectsByYear = () => {
    const grouped = {};
    projects.forEach(p => {
      const year = p.wedding_start_date ? new Date(p.wedding_start_date).getFullYear().toString() : 'No Date';
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(p);
    });
    return Object.entries(grouped).sort((a, b) => {
      if (a[0] === 'No Date') return 1;
      if (b[0] === 'No Date') return -1;
      return b[0] - a[0];
    });
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [albumsRes, projectsRes, eventsRes] = await Promise.all([
        workflowAPI.getAlbums(),
        workflowAPI.getProjects(),
        workflowAPI.getEvents(),
      ]);
      setAlbums(albumsRes.data);
      setProjects(projectsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const p = projects.find(pr => pr.id === projectId);
    return p ? `${p.project_code} - ${p.bride_name} & ${p.groom_name}` : null;
  };

  const getProjectEvents = (projectId) => events.filter(e => e.project_id === projectId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      project_id: formData.useProject ? formData.project_id : null,
      client_name: formData.client_name,
      event_type: formData.event_type || null,
      album_name: formData.album_name,
      notes: formData.notes || null,
    };

    if (!submitData.album_name) {
      toast.error('Album name is required');
      return;
    }
    if (!submitData.client_name) {
      toast.error('Client name is required');
      return;
    }

    try {
      await workflowAPI.createAlbum(submitData);
      setShowForm(false);
      setFormData({ project_id: '', client_name: '', event_type: '', album_name: '', notes: '', useProject: false, useEvent: false });
      toast.success('Album order created');
      fetchData();
    } catch (error) {
      console.error('Error creating album:', error);
      toast.error('Error creating album');
    }
  };

  const handleStageUpdate = async (albumId, field, value) => {
    try {
      await workflowAPI.updateAlbum(albumId, { [field]: value });
      fetchData();
    } catch (error) {
      console.error('Error updating album:', error);
    }
  };

  const handleDelete = async (albumId) => {
    if (!window.confirm('Delete this album order?')) return;
    try {
      await workflowAPI.deleteAlbum(albumId);
      toast.success('Album deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting album:', error);
    }
  };

  const handleLinkProject = async (albumId, projectId) => {
    try {
      const project = projects.find(p => p.id === projectId);
      const update = { project_id: projectId };
      if (project) {
        update.client_name = `${project.bride_name} & ${project.groom_name}`;
      }
      await workflowAPI.updateAlbum(albumId, update);
      setLinkingAlbum(null);
      toast.success('Linked to project');
      fetchData();
    } catch (error) {
      console.error('Error linking:', error);
    }
  };

  const handleUnlinkProject = async (albumId) => {
    try {
      await workflowAPI.updateAlbum(albumId, { project_id: '' });
      toast.success('Unlinked from project');
      fetchData();
    } catch (error) {
      console.error('Error unlinking:', error);
    }
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setFormData(prev => ({
        ...prev,
        project_id: projectId,
        client_name: `${project.bride_name} & ${project.groom_name}`,
      }));
    } else {
      setFormData(prev => ({ ...prev, project_id: projectId, client_name: '' }));
    }
  };

  const handleEventSelect = (eventType) => {
    setFormData(prev => ({
      ...prev,
      event_type: eventType,
      album_name: eventType ? `${eventType} Album` : prev.album_name,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return '#ef4444';
      case 'Photo Selection Done': return '#f59e0b';
      case 'In Design': return '#0ea5e9';
      case 'Client Review': return '#a855f7';
      case 'Approved': return '#10b981';
      case 'At Print': return '#6366f1';
      case 'Delivered': return '#059669';
      default: return '#6b7280';
    }
  };

  const getStageProgress = (album) => {
    let done = 0;
    STAGES.forEach(s => { if (album[s.key] === 'Y') done++; });
    return done;
  };

  const Wrap = embedded ? React.Fragment : Layout;

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-muted-foreground font-serif">Loading...</div>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="p-6 lg:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-serif tracking-tight" data-testid="albums-title">Album Processing</h1>
            <p className="text-muted-foreground mt-1">{albums.length} album order{albums.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium"
            data-testid="add-album-button"
          >
            <Plus className="h-5 w-5" />
            {showForm ? 'Cancel' : 'New Album Order'}
          </button>
        </div>

        {showForm && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="text-xl font-serif mb-4">New Album Order</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-4 mb-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.useProject}
                    onChange={(e) => setFormData(prev => ({ ...prev, useProject: e.target.checked, project_id: '', client_name: '' }))}
                    className="accent-gold"
                  />
                  Link to existing project
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.useProject ? (
                  <div>
                    <label className="block text-xs uppercase tracking-wide mb-1">Project</label>
                    <select
                      value={formData.project_id}
                      onChange={(e) => handleProjectSelect(e.target.value)}
                      className="w-full px-4 py-3 border border-input bg-background text-sm"
                      data-testid="album-project-select"
                    >
                      <option value="">Select Project</option>
                      {getProjectsByYear().map(([year, yearProjects]) => (
                        <optgroup key={year} label={`── ${year} ──`}>
                          {yearProjects.map(p => (
                            <option key={p.id} value={p.id}>{p.project_code} - {p.bride_name} & {p.groom_name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs uppercase tracking-wide mb-1">Client Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="e.g. Heena & Aniket"
                      className="w-full px-4 py-3 border border-input bg-background text-sm"
                      data-testid="album-client-input"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Album Name *</label>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.useEvent}
                        onChange={(e) => setFormData(prev => ({ ...prev, useEvent: e.target.checked, event_type: '', album_name: '' }))}
                        className="accent-gold"
                      />
                      Pick from event
                    </label>
                  </div>
                  {formData.useEvent && formData.useProject && formData.project_id ? (
                    <select
                      value={formData.event_type}
                      onChange={(e) => handleEventSelect(e.target.value)}
                      className="w-full px-4 py-3 border border-input bg-background text-sm"
                      data-testid="album-event-select"
                    >
                      <option value="">Select Event</option>
                      {getProjectEvents(formData.project_id).map(ev => (
                        <option key={ev.id} value={ev.event_type}>{ev.event_type}</option>
                      ))}
                    </select>
                  ) : formData.useEvent ? (
                    <select
                      value={formData.event_type}
                      onChange={(e) => handleEventSelect(e.target.value)}
                      className="w-full px-4 py-3 border border-input bg-background text-sm"
                    >
                      <option value="">Select Event Type</option>
                      {['Wedding', 'Wedding & Reception', 'Engagement', 'Sangeet', 'Haldi', 'Mehndi', 'Reception', 'Pre-wedding'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      required
                      value={formData.album_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, album_name: e.target.value }))}
                      placeholder="e.g. Wedding Album, Coffee Table Book"
                      className="w-full px-4 py-3 border border-input bg-background text-sm"
                      data-testid="album-name-input"
                    />
                  )}
                </div>
              </div>

              {formData.useProject && formData.project_id && (
                <p className="text-sm text-muted-foreground">Client: <span className="font-medium text-foreground">{formData.client_name}</span></p>
              )}

              <div>
                <label className="block text-xs uppercase tracking-wide mb-1">Notes</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special instructions..."
                  className="w-full px-4 py-3 border border-input bg-background text-sm"
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium"
                data-testid="submit-album"
              >
                Create Album Order
              </button>
            </form>
          </div>
        )}

        <div className="space-y-4">
          {albums.length === 0 ? (
            <div className="bg-card border border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">No album orders yet</p>
              <button onClick={() => setShowForm(true)} className="text-gold font-medium hover:underline">Create your first album order</button>
            </div>
          ) : (
            albums.map(album => {
              const isExpanded = expandedAlbum === album.id;
              const progress = getStageProgress(album);
              const statusColor = getStatusColor(album.overall_status);
              const linkedProject = getProjectName(album.project_id);

              return (
                <div key={album.id} className="bg-card border border-border overflow-hidden" style={{ borderLeftWidth: '4px', borderLeftColor: statusColor }} data-testid="album-card">
                  <div className="p-6 cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setExpandedAlbum(isExpanded ? null : album.id)}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {linkedProject && <span className="text-xs font-bold text-gold bg-gold/10 px-2 py-1">{linkedProject.split(' - ')[0]}</span>}
                          <StatusBadge status={album.overall_status} />
                          <span className="text-xs text-muted-foreground">{progress}/{STAGES.length} stages</span>
                        </div>
                        <h3 className="text-lg font-serif mb-1">{album.album_name}</h3>
                        <p className="text-sm text-muted-foreground">{album.client_name}</p>
                        {album.event_type && <p className="text-xs text-muted-foreground mt-1">{album.event_type}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(album.id); }}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                          data-testid="delete-album"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3 w-full bg-accent h-1.5">
                      <div className="bg-gold h-1.5 transition-all" style={{ width: `${(progress / STAGES.length) * 100}%` }} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border bg-accent/30 p-6 space-y-4">
                      {/* Link/Unlink project */}
                      <div className="flex items-center gap-3 pb-4 border-b border-border">
                        {album.project_id ? (
                          <>
                            <Link2 className="h-4 w-4 text-gold" />
                            <span className="text-sm">Linked to <span className="font-medium">{linkedProject}</span></span>
                            <button
                              onClick={() => handleUnlinkProject(album.id)}
                              className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs border border-border hover:border-destructive hover:text-destructive transition-colors"
                              data-testid="unlink-project"
                            >
                              <Unlink className="h-3 w-3" /> Unlink
                            </button>
                          </>
                        ) : linkingAlbum === album.id ? (
                          <div className="flex items-center gap-2 w-full">
                            <select
                              onChange={(e) => { if (e.target.value) handleLinkProject(album.id, e.target.value); }}
                              className="flex-1 px-3 py-2 border border-input bg-background text-sm"
                              data-testid="link-project-select"
                            >
                              <option value="">Select project to link...</option>
                              {getProjectsByYear().map(([year, yearProjects]) => (
                                <optgroup key={year} label={`── ${year} ──`}>
                                  {yearProjects.map(p => (
                                    <option key={p.id} value={p.id}>{p.project_code} - {p.bride_name} & {p.groom_name}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                            <button onClick={() => setLinkingAlbum(null)} className="px-3 py-2 text-xs border border-border hover:bg-accent">Cancel</button>
                          </div>
                        ) : (
                          <>
                            <Unlink className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Standalone order</span>
                            <button
                              onClick={() => setLinkingAlbum(album.id)}
                              className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-black/90 transition-all"
                              data-testid="link-project-button"
                            >
                              <Link2 className="h-3 w-3" /> Link to Project
                            </button>
                          </>
                        )}
                      </div>

                      {/* Stage toggles */}
                      <div className="space-y-3">
                        {STAGES.map((stage, idx) => {
                          const isDone = album[stage.key] === 'Y';
                          const dateField = `${stage.key}_date`;
                          return (
                            <div key={stage.key} className="flex items-center justify-between py-2">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${isDone ? 'bg-primary text-primary-foreground' : 'bg-accent text-muted-foreground'}`}>
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className={`text-sm font-medium ${isDone ? 'text-foreground' : 'text-muted-foreground'}`}>{stage.label}</p>
                                  {isDone && album[dateField] && <p className="text-xs text-muted-foreground">{album[dateField]}</p>}
                                </div>
                              </div>
                              <select
                                value={album[stage.key]}
                                onChange={(e) => handleStageUpdate(album.id, stage.key, e.target.value)}
                                className={`px-3 py-2 border text-sm ${isDone ? 'border-gold bg-gold/10' : 'border-input bg-background'}`}
                              >
                                <option value="Y">Done</option>
                                <option value="N">Not Done</option>
                              </select>
                            </div>
                          );
                        })}
                      </div>

                      {/* Notes */}
                      <div className="pt-3 border-t border-border">
                        <label className="block text-sm font-medium mb-2">Notes</label>
                        <textarea
                          value={album.notes || ''}
                          onChange={(e) => handleStageUpdate(album.id, 'notes', e.target.value)}
                          placeholder="Add notes..."
                          rows="2"
                          className="w-full px-4 py-3 border border-input bg-background text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Wrap>
  );
};

export default AlbumProcessing;
